import { useEffect, useState } from 'react';
import { useFlags as useLDFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import Config from 'src/common/Config';
import useFlagOverrides from './useFlagOverrides';

// Routing must never hang on LaunchDarkly being reachable. If the client has not
// initialised by now, carry on with default flag values rather than holding the app.
const FLAGS_READY_TIMEOUT_MS = 5000;

export type Flags = {
  showDataUploadCounts: boolean;
  flagEnabledComplianceChecking: boolean;
  flagEnabledDocumentEdit: boolean;
  flagEnabledDocumentRename: boolean;
  flagEnabledMaintenanceMode: boolean;
  flagEnabledAgendaMeetingNoteReadOnly: boolean;
  flagEnabledBotMigration: boolean;
  flagEnabledObjectivesRework: boolean;
  flagEnabledBulkValControlCentre: boolean;
  flagEnabledMeetingNoteCitations: boolean;
  flagEnabledSiestaSteering: boolean;
  flagEnabledSiestaChats: boolean;
  flagEnabledSiestaAsyncGeneration: boolean;
  flagEnabledBatchCrudDataUpload: boolean;
  flagEnabledInlineEdit: boolean;
  flagEnabledFamilyFlowFrontend: boolean;
  flagEnabledIndividualDetailsRework: boolean;
  flagEnabledPinnedNationalityResidencyOptions: boolean;
  flagEnabledFamilyScreenRework: boolean;
  flagEnabledFinancialRework: boolean;
  flagEnabledFinancialItemOwners: boolean;
  flagEnabledAssetFeedIndicators: boolean;
  flagEnabledFundHoldingsUnderSummary: boolean;
  flagEnabledResponsiveMenus: boolean;
  flagEnabledLlmDispatcherAdmin: boolean;
  flagEnabledResumeCurrentChat: boolean;
  flagEnabledAdvisorSortAndFilter: boolean;
  flagEnabledClientSearchFullName: boolean;
  flagEnabledCashflowModelling: boolean;
  flagEnabledFamilyCashflowModelling: boolean;
  flagEnabledFamilyCashflowsRevamp: boolean;
  flagEnabledFamilyMeetingsRework: boolean;
  flagEnabledIndividualMeetingsRework: boolean;
  flagEnabledCreateUserModalRefactor: boolean;
  flagEnabledCreateUserModalBranding: boolean;
  flagEnabledShowNonConflictingFields: boolean;
  flagEnabledFamilyDocumentsRevamp: boolean;
  flagEnabledPaginatedClients: boolean;
  flagEnabledRegenerateTranscriptSummary: boolean;
  flagEnabledPreventAddConflicts: boolean;
  flagEnabledSiestaEmailDrafting: boolean;
  flagEnabledSiestaClientDataEditing: boolean;
  flagEnabledSiestaOutlookEmailActions: boolean;
  flagEnabledSiestaOutlookEmailReading: boolean;
  flagEnabledSiestaIndividualChatScope: boolean;
  flagEnabledSiestaMemoryProposals: boolean;
  flagEnabledSiestaSkillProposals: boolean;
  flagEnabledSiestaSaveToDocuments: boolean;
  flagEnabledVerifyOnDemand: boolean;
  flagEnabledSharepointUpload: boolean;
  flagEnabledOnboardingInvite: boolean;
  flagEnabledNotifications: boolean;
  flagEnabledHomeDashboard: boolean;
  flagEnabledDevTools: boolean;
  flagEnabledUnblockMeetingNotes: boolean;
  flagEnabledAdminAdviserSignatures: boolean;
  flagEnabledDataExportMemberSelect: boolean;
  flagEnabledUsageMetrics: boolean;
  flagEnabledCustomSkillFiles: boolean;
  flagEnabledLegacyDocConversion: boolean;
};

/**
 * Whether LaunchDarkly has finished initialising.
 *
 * Until it has, `useFlags()` reports every flag as `undefined`, which reads as "off".
 * Anything that *routes* on a flag value must wait for this, or it will briefly act on
 * a flag that is not really off - e.g. deciding an onboarding route does not exist and
 * redirecting an invited prospect to login.
 */
export function useFlagsReady(): boolean {
  const ldClient = useLDClient();
  const [hasTimedOut, setHasTimedOut] = useState(false);

  useEffect(() => {
    if (ldClient) return;
    const timer = setTimeout(() => setHasTimedOut(true), FLAGS_READY_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [ldClient]);

  return ldClient !== undefined || hasTimedOut;
}

export function useFlags(): Flags {
  const ldFlags = useLDFlags<Flags>();
  const { overrides } = useFlagOverrides();
  if (!Config.isDev()) return ldFlags;
  return { ...ldFlags, ...overrides };
}
